// Storing a price with its previous close, choosing a stock's price source, the tracked symbol list
// (holdings plus stocks sold in the last 75 days) and the daily value snapshot. Ported from v1's
// setPx(), stockProvider(), heldSheetSymbols() and snapshot().
/* eslint-disable @typescript-eslint/no-explicit-any */
import { isoOf, now, today } from './clock';
import { isSgx, meta, type Ccy, type State } from './state';
import { compute, totals } from './positions';
import { cashTotal } from './income';

/** Stores a live price with its previous close. When a source gives no previous close, the last price
 *  seen on an earlier day stands in for it. */
export function setPx(S: State, k: string, price: number, ccy: Ccy, prev?: number | null) {
  const o = S.prices[k], d = today(); let pv: number | null = (prev as number) > 0 ? +(prev as number) : null;
  if (!pv && o && o.ccy === ccy) { if (o.at && isoOf(o.at) < d) pv = o.price; else if ((o.prev as number) > 0 && o.pd === d) pv = o.prev as number; }
  S.prices[k] = { price, ccy, at: now(), src: 'live', prev: pv, pd: d };
}

/** A typed price (the price box in a holdings table). Keeps today's previous close, as v1 did. */
export function setManualPrice(S: State, k: string, x: number, ccy: Ccy, factor?: number) {
  const o = S.prices[k], d = today();
  const pv = o && o.ccy === ccy ? (o.pd === d && (o.prev as number) > 0 ? o.prev : o.at && isoOf(o.at) < d ? o.price : null) : null;
  S.prices[k] = { price: factor ? x / factor : x, ccy, at: now(), src: 'manual', prev: pv ?? null, pd: d };
}

/** Where a stock's price comes from. v2: 'sgx' (Yahoo, then SGX's own data), 'us' (Finnhub, then Yahoo),
 *  or null for "None, I'll enter prices myself". Same decision as v1's stockProvider(), with the sheet
 *  replaced by the SGX price feed and Finnhub always available through the server key (Q5, Q6). */
export function stockProvider(S: State, p: { key: string; sym: string }): 'sgx' | 'us' | null {
  const m = meta(S, p.key);
  if (m.provider === 'none') return null;
  if (m.provider === 'sheet' || m.provider === 'av' || m.provider === 'sgx') return 'sgx';
  if (m.provider === 'finnhub') return 'us';
  return isSgx(S, p) ? 'sgx' : 'us';
}

/** The stocks to keep prices and dividend checks for: everything held, plus stocks sold in the last 75 days.
 *  SGX stocks get .SI, as the old price sheet listed them. */
export function trackedStockSymbols(S: State): string[] {
  const cut = isoOf(now() - 75 * 864e5), recent = new Set(S.txns.filter((t) => t.type === 'stock' && t.side === 'sell' && t.date >= cut).map((t) => t.sym));
  return compute(S).filter((p) => p.type === 'stock' && (p.qty > 0 || recent.has(p.sym))).map((p) => (isSgx(S, p) ? p.sym + '.SI' : p.sym));
}

/** The day's value point (always in SGD, with that day's USD/SGD rate), or null when it's skipped:
 *  no trades and no cash, or any holding without a price. Replaces today's earlier point in S.history. */
export function snapshot(S: State): any | null {
  if (!S.txns.length && !S.cash.length) return null;
  const sg: State = { ...S, display: 'SGD' };
  const ps = compute(sg), t = totals(ps); if (t.missing) return null;
  const cash = cashTotal(sg), d = today();
  const pt = { d, sgd: +(t.mv + cash).toFixed(2), cost: +t.cost.toFixed(2), cash: +cash.toFixed(2), stock: +t.stockMv.toFixed(2), crypto: +t.cryptoMv.toFixed(2), metal: +(t.metalMv || 0).toFixed(2), ...(t.bondMv ? { bond: +t.bondMv.toFixed(2) } : {}), fx: S.fx ? S.fx.rate : null };
  const i = S.history.findIndex((h) => h.d === d); if (i >= 0) S.history[i] = pt; else { S.history.push(pt); S.history.sort((a, b) => (a.d < b.d ? -1 : 1)); }
  return pt;
}
